
import React, { useState, useEffect } from 'react'
import { X, Plus, Trash2, Upload, Camera, FileText, MapPin, Cloud, Sun, CloudRain, Users, Wrench, AlertTriangle, CheckCircle, Paperclip, Calculator, DollarSign, TrendingUp } from 'lucide-react'
import { useDiarioObra } from '../hooks/useDiarioObra'

interface DiarioObraFormProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: any) => Promise<void>
  initialData?: any
  title: string
}

const DiarioObraForm: React.FC<DiarioObraFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  title
}) => {
  const { 
    getObrasDisponiveis, 
    getFuncionariosDisponiveis, 
    getEquipamentosDisponiveis, 
    createEquipamento, 
    getAtividadesPlanejamento,
    getOrcamentosAprovados,
    getValoresEtapaOrcamento,
    getValoresPlanejamento,
    updateAtividade // 🔥 NOVA FUNÇÃO PARA ATUALIZAR STATUS
  } = useDiarioObra()
  
  const [obras, setObras] = useState<any[]>([])
  const [funcionarios, setFuncionarios] = useState<any[]>([])
  const [equipamentos, setEquipamentos] = useState<any[]>([])
  const [etapasDisponiveis, setEtapasDisponiveis] = useState<any[]>([])
  const [atividadesPlanejamento, setAtividadesPlanejamento] = useState<any[]>([])
  // 🔥 NOVOS ESTADOS PARA INTEGRAÇÃO COM ORÇAMENTO E PLANEJAMENTO
  const [orcamentosAprovados, setOrcamentosAprovados] = useState<any[]>([])
  const [valoresEtapaOrcamento, setValoresEtapaOrcamento] = useState<any>(null)
  const [valoresPlanejamento, setValoresPlanejamento] = useState<any[]>([]) // 🔥 NOVO ESTADO
  
  const [formData, setFormData] = useState({
    obraId: '',
    centroCusto: '',
    tipo: 'diario' as const,
    data: '',
    responsavelId: '',
    local: '',
    observacoes: '',
    // 🔥 NOVO CAMPO PARA ORÇAMENTO
    orcamentoId: ''
  })

  const [clima, setClima] = useState<any>({})
  const [checklistSeguranca, setChecklistSeguranca] = useState<any[]>([
    { item: 'EPIs obrigatórios', verificado: false },
    { item: 'Sinalização da obra', verificado: false },
    { item: 'Isolamento da área', verificado: false },
    { item: 'Verificação de equipamentos', verificado: false }
  ])
  const [maoObra, setMaoObra] = useState<any[]>([])
  const [equipamentosUsados, setEquipamentosUsados] = useState<any[]>([])
  const [atividadesManuais, setAtividadesManuais] = useState<any[]>([])
  const [naoConformidades, setNaoConformidades] = useState<any[]>([])
  const [fotos, setFotos] = useState<any[]>([])
  const [documentos, setDocumentos] = useState<any[]>([])
  const [anexos, setAnexos] = useState<any[]>([])
  const [medicaoAtividades, setMedicaoAtividades] = useState<any[]>([])
  const [medicaoEtapa, setMedicaoEtapa] = useState<any>({})
  const [materiaisUtilizados, setMateriaisUtilizados] = useState<any[]>([])
  
  const [showEquipamentoForm, setShowEquipamentoForm] = useState(false)
  const [novoEquipamento, setNovoEquipamento] = useState({
    nome: '',
    tipo: '',
    marca: '',
    modelo: '',
    valorHora: 0
  })

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      const [obrasData, funcionariosData, equipamentosData, orcamentosData] = await Promise.all([
        getObrasDisponiveis(),
        getFuncionariosDisponiveis(),
        getEquipamentosDisponiveis(),
        getOrcamentosAprovados()
      ])
      
      setObras(obrasData)
      setFuncionarios(funcionariosData)
      setEquipamentos(equipamentosData)
      setOrcamentosAprovados(orcamentosData)
    }
    
    if (isOpen) {
      loadData()
    }
  }, [isOpen])

  // Load etapas when obra changes
  useEffect(() => {
    if (formData.obraId) {
      const obraSelecionada = obras.find(o => o._id === formData.obraId)
      if (obraSelecionada) {
        setEtapasDisponiveis(obraSelecionada.etapas || [])
        
        // Auto-fill obra data
        setFormData(prev => ({
          ...prev,
          local: obraSelecionada.localObra || '',
          centroCusto: obraSelecionada.centroCusto || `CC-${obraSelecionada.numeroContrato}`
        }))

        // Load materiais from planejamento
        const materiaisPlanejamento = obraSelecionada.etapas?.flatMap((etapa: any) => 
          etapa.insumos?.map((insumo: any) => ({
            insumoId: insumo.insumoId,
            nome: insumo.nome,
            quantidadePlanejada: insumo.quantidade,
            quantidadeUsada: 0,
            unidade: insumo.unidade
          })) || []
        ) || []
        
        setMateriaisUtilizados(materiaisPlanejamento)

        // 🔥 CARREGAR VALORES DO PLANEJAMENTO
        loadValoresPlanejamento()
      }
    }
  }, [formData.obraId, obras])

  // 🔥 CARREGAR ATIVIDADES DO PLANEJAMENTO SEMANAL
  useEffect(() => {
    if (formData.obraId && medicaoEtapa.etapaId) {
      loadAtividadesPlanejamento()
    }
  }, [formData.obraId, medicaoEtapa.etapaId])

  // 🔥 CARREGAR VALORES DA ETAPA DO ORÇAMENTO
  useEffect(() => {
    if (formData.orcamentoId && medicaoEtapa.etapaId) {
      loadValoresEtapaOrcamento()
    }
  }, [formData.orcamentoId, medicaoEtapa.etapaId])

  const loadAtividadesPlanejamento = async () => {
    try {
      const atividades = await getAtividadesPlanejamento(formData.obraId, medicaoEtapa.etapaId)
      setAtividadesPlanejamento(atividades)
    } catch (error) {
      console.error('Erro ao carregar atividades do planejamento:', error)
    }
  }

  // 🔥 NOVA FUNÇÃO: CARREGAR VALORES DA ETAPA DO ORÇAMENTO
  const loadValoresEtapaOrcamento = async () => {
    try {
      const valores = await getValoresEtapaOrcamento(formData.orcamentoId, medicaoEtapa.etapaId)
      setValoresEtapaOrcamento(valores)
    } catch (error) {
      console.error('Erro ao carregar valores da etapa do orçamento:', error)
    }
  }

  // 🔥 NOVA FUNÇÃO: CARREGAR VALORES DO PLANEJAMENTO
  const loadValoresPlanejamento = async () => {
    try {
      const valores = await getValoresPlanejamento(formData.obraId)
      setValoresPlanejamento(valores)
    } catch (error) {
      console.error('Erro ao carregar valores do planejamento:', error)
    }
  }

  // 🔥 NOVA FUNÇÃO: ATUALIZAR STATUS DA ATIVIDADE
  const handleUpdateAtividadeStatus = async (atividadeId: string, novoStatus: string) => {
    try {
      await updateAtividade(atividadeId, { status: novoStatus })
      
      // Atualizar a lista local de atividades
      setAtividadesPlanejamento(prev => 
        prev.map(atividade => 
          atividade._id === atividadeId 
            ? { ...atividade, status: novoStatus }
            : atividade
        )
      )
      
      console.log(`✅ Status da atividade ${atividadeId} atualizado para: ${novoStatus}`)
    } catch (error) {
      console.error('Erro ao atualizar status da atividade:', error)
    }
  }

  // Initialize form with existing data
  useEffect(() => {
    if (initialData) {
      setFormData({
        obraId: initialData.obraId || '',
        centroCusto: initialData.centroCusto || '',
        tipo: initialData.tipo || 'diario',
        data: initialData.data?.split('T')[0] || '',
        responsavelId: initialData.responsavelId || '',
        local: initialData.local || '',
        observacoes: initialData.observacoes || '',
        orcamentoId: initialData.orcamentoId || ''
      })
      
      setClima(initialData.clima || {})
      setChecklistSeguranca(initialData.checklistSeguranca || [])
      setMaoObra(initialData.maoObra || [])
      setEquipamentosUsados(initialData.equipamentos || [])
      setAtividadesManuais(initialData.atividadesManuais || [])
      setNaoConformidades(initialData.naoConformidades || [])
      setFotos(initialData.fotos || [])
      setDocumentos(initialData.documentos || [])
      setAnexos(initialData.anexos || [])
      setMedicaoAtividades(initialData.medicaoAtividades || [])
      setMedicaoEtapa(initialData.medicaoEtapa || {})
      setMateriaisUtilizados(initialData.materiaisUtilizados || [])
      setValoresEtapaOrcamento(initialData.valoresEtapaOrcamento || null)
      setValoresPlanejamento(initialData.valoresPlanejamento || [])
    }
  }, [initialData])

  // Add mão de obra
  const addMaoObra = () => {
    setMaoObra([...maoObra, {
      funcionarioId: '',
      nome: '',
      funcao: '',
      horas: 0,
      valorHora: 0,
      valorTotal: 0
    }])
  }

  // Update mão de obra
  const updateMaoObra = (index: number, field: string, value: any) => {
    const newMaoObra = [...maoObra]
    newMaoObra[index] = { ...newMaoObra[index], [field]: value }
    
    if (field === 'funcionarioId') {
      const funcionario = funcionarios.find(f => f._id === value)
      if (funcionario) {
        newMaoObra[index].nome = funcionario.nome
        newMaoObra[index].funcao = funcionario.funcao
        newMaoObra[index].valorHora = funcionario.valorRemuneracao
      }
    }
    
    if (field === 'horas' || field === 'valorHora') {
      newMaoObra[index].valorTotal = newMaoObra[index].horas * newMaoObra[index].valorHora
    }
    
    setMaoObra(newMaoObra)
  }

  // Add equipamento usado
  const addEquipamentoUsado = () => {
    setEquipamentosUsados([...equipamentosUsados, {
      equipamentoId: '',
      nome: '',
      horas: 0,
      valorHora: 0,
      valorTotal: 0
    }])
  }

  // Update equipamento usado
  const updateEquipamentoUsado = (index: number, field: string, value: any) => {
    const newEquipamentos = [...equipamentosUsados]
    newEquipamentos[index] = { ...newEquipamentos[index], [field]: value }
    
    if (field === 'equipamentoId') {
      const equipamento = equipamentos.find(e => e._id === value)
      if (equipamento) {
        newEquipamentos[index].nome = equipamento.nome
        newEquipamentos[index].valorHora = equipamento.valorHora
      }
    }
    
    if (field === 'horas' || field === 'valorHora') {
      newEquipamentos[index].valorTotal = newEquipamentos[index].horas * newEquipamentos[index].valorHora
    }
    
    setEquipamentosUsados(newEquipamentos)
  }

  // 🔥 NOVA FUNÇÃO: ADD ATIVIDADE MANUAL COM STATUS E OBSERVAÇÕES
  const addAtividadeManual = () => {
    setAtividadesManuais([...atividadesManuais, {
      descricao: '',
      responsavel: '',
      etapaId: '',
      subetapaId: '',
      percentualConcluido: 0,
      status: 'planejada', // 🔥 NOVO CAMPO
      observacoes: '' // 🔥 NOVO CAMPO
    }])
  }

  // 🔥 NOVA FUNÇÃO: UPDATE ATIVIDADE MANUAL
  const updateAtividadeManual = (index: number, field: string, value: any) => {
    const newAtividades = [...atividadesManuais]
    newAtividades[index] = { ...newAtividades[index], [field]: value }
    setAtividadesManuais(newAtividades)
  }

  // Add não conformidade
  const addNaoConformidade = () => {
    setNaoConformidades([...naoConformidades, {
      descricao: '',
      gravidade: 'baixa',
      responsavel: '',
      prazoCorrecao: ''
    }])
  }

  // 🔥 HANDLE FILE UPLOAD (FOTOS E ARQUIVOS)
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>, type: 'foto' | 'documento' | 'anexo') => {
    const files = Array.from(event.target.files || [])
    const newFiles = files.map(file => ({
      nome: file.name,
      url: URL.createObjectURL(file),
      tipo: file.type,
      tamanho: file.size,
      descricao: '',
      dataUpload: new Date().toISOString()
    }))
    
    if (type === 'foto') {
      setFotos([...fotos, ...newFiles])
    } else if (type === 'documento') {
      setDocumentos([...documentos, ...newFiles])
    } else {
      setAnexos([...anexos, ...newFiles])
    }
  }

  // Remove anexo
  const removeAnexo = (index: number, type: 'foto' | 'documento' | 'anexo') => {
    if (type === 'foto') {
      setFotos(fotos.filter((_, i) => i !== index))
    } else if (type === 'documento') {
      setDocumentos(documentos.filter((_, i) => i !== index))
    } else {
      setAnexos(anexos.filter((_, i) => i !== index))
    }
  }

  // Create new equipamento
  const handleCreateEquipamento = async () => {
    try {
      const equipamento = await createEquipamento({
        ...novoEquipamento,
        status: 'disponivel',
        numeroSerie: `EQ-${Date.now()}`,
        observacoes: 'Cadastrado via diário de obra'
      })
      
      setEquipamentos([...equipamentos, equipamento])
      setNovoEquipamento({ nome: '', tipo: '', marca: '', modelo: '', valorHora: 0 })
      setShowEquipamentoForm(false)
    } catch (error) {
      console.error('Erro ao criar equipamento:', error)
    }
  }

  // 🔥 CALCULAR VALOR MEDIDO BASEADO NO ORÇAMENTO, PLANEJAMENTO OU PERCENTUAL
  const calculateValorMedido = () => {
    let valorTotal = 0
    
    // 🔥 PRIORIDADE 1: Usar valores do orçamento se disponível
    if (valoresEtapaOrcamento && medicaoEtapa.percentualConcluido) {
      valorTotal = valoresEtapaOrcamento.valorTotal * (medicaoEtapa.percentualConcluido / 100)
      return valorTotal
    }
    
    // 🔥 PRIORIDADE 2: Usar valores do planejamento se disponível
    if (valoresPlanejamento.length > 0 && medicaoEtapa.etapaId) {
      const etapaPlanejamento = valoresPlanejamento.find(e => e.etapaId === medicaoEtapa.etapaId)
      if (etapaPlanejamento && medicaoEtapa.percentualConcluido) {
        valorTotal = etapaPlanejamento.valorOrcado * (medicaoEtapa.percentualConcluido / 100)
        return valorTotal
      }
    }
    
    // PRIORIDADE 3: Valor da etapa principal (método anterior)
    if (medicaoEtapa.etapaId && medicaoEtapa.percentualConcluido) {
      const etapa = etapasDisponiveis.find(e => e.id === medicaoEtapa.etapaId)
      if (etapa) {
        valorTotal += (etapa.valorOrcado || 0) * (medicaoEtapa.percentualConcluido / 100)
      }
    }
    
    // Valor das atividades do planejamento
    atividadesPlanejamento.forEach(atividade => {
      if (atividade.valorOrcado && atividade.progresso) {
        valorTotal += atividade.valorOrcado * (atividade.progresso / 100)
      }
    })
    
    return valorTotal
  }

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const obraSelecionada = obras.find(o => o._id === formData.obraId)
    if (!obraSelecionada) {
      alert('Selecione uma obra')
      return
    }

    const responsavel = funcionarios.find(f => f._id === formData.responsavelId)
    if (!responsavel) {
      alert('Selecione um responsável')
      return
    }

    // 🔥 CALCULAR PROGRESSO GERAL E VALOR DA MEDIÇÃO
    const progressoGeral = medicaoEtapa.percentualConcluido || 0
    const valorMedicao = calculateValorMedido()

    const submitData = {
      ...formData,
      nomeObra: obraSelecionada.nomeObra,
      numeroContrato: obraSelecionada.numeroContrato,
      clienteNome: obraSelecionada.clienteNome || 'N/A',
      responsavelNome: responsavel.nome,
      data: new Date(formData.data).toISOString(),
      clima,
      checklistSeguranca,
      maoObra,
      equipamentos: equipamentosUsados,
      atividadesManuais,
      atividadesPlanejamento,
      naoConformidades,
      fotos,
      documentos,
      anexos,
      medicaoAtividades,
      medicaoEtapa: {
        ...medicaoEtapa,
        valorMedido: valorMedicao
      },
      materiaisUtilizados,
      progressoGeral,
      valorMedicao,
      projecaoRecebimento: valorMedicao * 1.1, // Exemplo de cálculo
      projecaoPagamento: valorMedicao * 0.9, // Exemplo de cálculo
      status: 'rascunho',
      // 🔥 INCLUIR VALORES DA ETAPA DO ORÇAMENTO E PLANEJAMENTO
      valoresEtapaOrcamento,
      valoresPlanejamento
    }

    await onSubmit(submitData)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          {/* Informações Básicas */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <MapPin size={20} />
              Informações Básicas
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Projeto *
                </label>
                <select
                  value={formData.obraId}
                  onChange={(e) => setFormData(prev => ({ ...prev, obraId: e.target.value }))}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecionar projeto...</option>
                  {obras.map(obra => (
                    <option key={obra._id} value={obra._id}>
                      {obra.nomeObra} - {obra.numeroContrato}
                    </option>
                  ))}
                </select>
              </div>

              {/* 🔥 NOVO CAMPO: ORÇAMENTO APROVADO */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Orçamento Aprovado (Opcional)
                </label>
                <select
                  value={formData.orcamentoId}
                  onChange={(e) => setFormData(prev => ({ ...prev, orcamentoId: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecionar orçamento...</option>
                  {orcamentosAprovados.map(orcamento => (
                    <option key={orcamento._id} value={orcamento._id}>
                      {orcamento.numero} - {orcamento.nomeCliente}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Centro de Custo *
                </label>
                <input
                  type="text"
                  value={formData.centroCusto}
                  onChange={(e) => setFormData(prev => ({ ...prev, centroCusto: e.target.value }))}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: CC-001, OBRA-2024-01"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo *
                </label>
                <select
                  value={formData.tipo}
                  onChange={(e) => setFormData(prev => ({ ...prev, tipo: e.target.value as any }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="diario">Diário</option>
                  <option value="semanal">Semanal</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data *
                </label>
                <input
                  type="date"
                  value={formData.data}
                  onChange={(e) => setFormData(prev => ({ ...prev, data: e.target.value }))}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Responsável *
                </label>
                <select
                  value={formData.responsavelId}
                  onChange={(e) => setFormData(prev => ({ ...prev, responsavelId: e.target.value }))}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecionar responsável...</option>
                  {funcionarios.map(funcionario => (
                    <option key={funcionario._id} value={funcionario._id}>
                      {funcionario.nome} - {funcionario.funcao}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Local *
                </label>
                <input
                  type="text"
                  value={formData.local}
                  onChange={(e) => setFormData(prev => ({ ...prev, local: e.target.value }))}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* 🔥 CLIMA POR PERÍODO (DIÁRIO) OU SEMANAL */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Cloud size={20} />
              Condições Climáticas
            </h3>
            
            {formData.tipo === 'diario' ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {['manha', 'tarde', 'noite'].map(periodo => (
                  <div key={periodo}>
                    <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                      {periodo === 'manha' ? 'Manhã' : periodo === 'tarde' ? 'Tarde' : 'Noite'}
                    </label>
                    <select
                      value={clima.periodos?.[periodo] || ''}
                      onChange={(e) => setClima(prev => ({ 
                        ...prev, 
                        periodos: { ...prev.periodos, [periodo]: e.target.value } 
                      }))}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecionar...</option>
                      <option value="sol">☀️ Sol</option>
                      <option value="nublado">☁️ Nublado</option>
                      <option value="chuva">🌧️ Chuva</option>
                      <option value="garoa">🌦️ Garoa</option>
                    </select>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {['segunda', 'terca', 'quarta', 'quinta', 'sexta'].map(dia => (
                  <div key={dia}>
                    <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                      {dia}
                    </label>
                    <select
                      value={clima[dia] || ''}
                      onChange={(e) => setClima(prev => ({ ...prev, [dia]: e.target.value }))}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecionar...</option>
                      <option value="sol">☀️ Sol</option>
                      <option value="nublado">☁️ Nublado</option>
                      <option value="chuva">🌧️ Chuva</option>
                    </select>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Medição da Etapa */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Calculator size={20} />
              Medição da Etapa
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Etapa
                </label>
                <select
                  value={medicaoEtapa.etapaId || ''}
                  onChange={(e) => {
                    const etapa = etapasDisponiveis.find(et => et.id === e.target.value)
                    setMedicaoEtapa(prev => ({
                      ...prev,
                      etapaId: e.target.value,
                      valorEtapa: etapa?.valorOrcado || 0
                    }))
                  }}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecionar etapa...</option>
                  {etapasDisponiveis.map(etapa => (
                    <option key={etapa.id} value={etapa.id}>
                      {etapa.numero} - {etapa.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Percentual Concluído (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={medicaoEtapa.percentualConcluido || ''}
                  onChange={(e) => {
                    const percentual = Number(e.target.value)
                    setMedicaoEtapa(prev => ({
                      ...prev,
                      percentualConcluido: percentual
                    }))
                  }}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor Medido (Calculado)
                </label>
                <input
                  type="text"
                  value={`R$ ${calculateValorMedido().toFixed(2)}`}
                  readOnly
                  className="w-full p-2 border border-gray-300 rounded-md bg-gray-100"
                />
              </div>
            </div>

            {/* 🔥 EXIBIR VALORES DISCRIMINADOS DO ORÇAMENTO */}
            {valoresEtapaOrcamento && (
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
                  <DollarSign size={18} />
                  Valores da Etapa (Orçamento: {orcamentosAprovados.find(o => o._id === formData.orcamentoId)?.numero})
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div className="bg-white rounded-lg p-3 border border-blue-300">
                    <div className="text-center">
                      <span className="text-sm font-medium text-blue-800">Total da Etapa:</span>
                      <div className="text-xl font-bold text-blue-900">
                        R$ {valoresEtapaOrcamento.valorTotal.toFixed(2)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-green-50 rounded-lg p-3 border border-green-300">
                    <span className="text-sm font-medium text-green-700">Material:</span>
                    <div className="text-lg font-bold text-green-900">
                      R$ {valoresEtapaOrcamento.discriminacao.material.toFixed(2)}
                    </div>
                  </div>
                  
                  <div className="bg-orange-50 rounded-lg p-3 border border-orange-300">
                    <span className="text-sm font-medium text-orange-700">Equipamento:</span>
                    <div className="text-lg font-bold text-orange-900">
                      R$ {valoresEtapaOrcamento.discriminacao.equipamento.toFixed(2)}
                    </div>
                  </div>
                  
                  <div className="bg-purple-50 rounded-lg p-3 border border-purple-300">
                    <span className="text-sm font-medium text-purple-700">Mão de Obra:</span>
                    <div className="text-lg font-bold text-purple-900">
                      R$ {valoresEtapaOrcamento.discriminacao.maoObra.toFixed(2)}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-300">
                    <span className="text-sm font-medium text-gray-700">Composições:</span>
                    <div className="text-lg font-bold text-gray-900">
                      R$ {valoresEtapaOrcamento.discriminacao.composicao.toFixed(2)}
                    </div>
                  </div>
                </div>

                <div className="mt-3 text-center">
                  <p className="text-sm text-blue-700">
                    💡 Valor da medição calculado automaticamente: {medicaoEtapa.percentualConcluido || 0}% de R$ {valoresEtapaOrcamento.valorTotal.toFixed(2)} = 
                    <strong className="text-blue-900"> R$ {calculateValorMedido().toFixed(2)}</strong>
                  </p>
                </div>
              </div>
            )}

            {/* 🔥 EXIBIR VALORES DO PLANEJAMENTO */}
            {valoresPlanejamento.length > 0 && (
              <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-green-900 mb-3 flex items-center gap-2">
                  <TrendingUp size={18} />
                  Valores do Planejamento da Obra
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {valoresPlanejamento.map((etapa, index) => (
                    <div key={index} className="bg-white rounded-lg p-3 border border-green-300">
                      <div className="text-sm font-medium text-green-700">{etapa.etapaNome}</div>
                      <div className="text-lg font-bold text-green-900">
                        R$ {etapa.valorMedido.toFixed(2)}
                      </div>
                      <div className="text-xs text-green-600">
                        {etapa.percentualConcluido}% de R$ {etapa.valorOrcado.toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* 🔥 ATIVIDADES DO PLANEJAMENTO SEMANAL COM ALTERAÇÃO DE STATUS */}
          {atividadesPlanejamento.length > 0 && (
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-4">Atividades do Planejamento Semanal</h3>
              
              <div className="space-y-3">
                {atividadesPlanejamento.map((atividade, index) => (
                  <div key={atividade._id} className="bg-white p-4 rounded border">
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                        <h4 className="font-medium">{atividade.descricao}</h4>
                        <p className="text-sm text-gray-600">
                          Responsável: {atividade.funcionarioResponsavel} | 
                          Progresso: {atividade.progresso}%
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm font-medium">
                            Valor: R$ {((atividade.valorOrcado || 0) * (atividade.progresso / 100)).toFixed(2)}
                          </p>
                        </div>
                        
                        {/* 🔥 DROPDOWN PARA ALTERAR STATUS */}
                        <div className="min-w-[140px]">
                          <select
                            value={atividade.status}
                            onChange={(e) => handleUpdateAtividadeStatus(atividade._id, e.target.value)}
                            className={`w-full px-3 py-1 text-xs font-semibold rounded-full border-0 focus:ring-2 focus:ring-blue-500 ${
                              atividade.status === 'concluida' ? 'bg-green-100 text-green-800' :
                              atividade.status === 'em_andamento' ? 'bg-blue-100 text-blue-800' :
                              atividade.status === 'pausada' ? 'bg-yellow-100 text-yellow-800' :
                              atividade.status === 'cancelada' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}
                          >
                            <option value="pendente">PENDENTE</option>
                            <option value="em_andamento">EM ANDAMENTO</option>
                            <option value="pausada">PAUSADA</option>
                            <option value="concluida">CONCLUÍDA</option>
                            <option value="cancelada">CANCELADA</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 🔥 ATIVIDADES MANUAIS COM STATUS E OBSERVAÇÕES */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <CheckCircle size={20} />
                Atividades Manuais
              </h3>
              <button
                type="button"
                onClick={addAtividadeManual}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2"
              >
                <Plus size={16} />
                Adicionar Atividade
              </button>
            </div>

            <div className="space-y-4">
              {atividadesManuais.map((atividade, index) => (
                <div key={index} className="bg-white p-4 rounded border">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Descrição *
                      </label>
                      <input
                        type="text"
                        value={atividade.descricao}
                        onChange={(e) => updateAtividadeManual(index, 'descricao', e.target.value)}
                        required
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        placeholder="Descrição da atividade"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Responsável
                      </label>
                      <select
                        value={atividade.responsavel}
                        onChange={(e) => updateAtividadeManual(index, 'responsavel', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Selecionar...</option>
                        {funcionarios.map(funcionario => (
                          <option key={funcionario._id} value={funcionario.nome}>
                            {funcionario.nome}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status *
                      </label>
                      <select
                        value={atividade.status}
                        onChange={(e) => updateAtividadeManual(index, 'status', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="planejada">Planejada</option>
                        <option value="em_andamento">Em Andamento</option>
                        <option value="concluida">Concluída</option>
                        <option value="pausada">Pausada</option>
                        <option value="cancelada">Cancelada</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        % Concluído
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={atividade.percentualConcluido}
                        onChange={(e) => updateAtividadeManual(index, 'percentualConcluido', Number(e.target.value))}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Observações
                    </label>
                    <textarea
                      value={atividade.observacoes}
                      onChange={(e) => updateAtividadeManual(index, 'observacoes', e.target.value)}
                      rows={2}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      placeholder="Observações sobre a atividade..."
                    />
                  </div>

                  <div className="flex justify-end mt-4">
                    <button
                      type="button"
                      onClick={() => setAtividadesManuais(atividadesManuais.filter((_, i) => i !== index))}
                      className="text-red-600 hover:text-red-800 flex items-center gap-1"
                    >
                      <Trash2 size={16} />
                      Remover
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 🔥 ANEXOS (FOTOS E ARQUIVOS) */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Paperclip size={20} />
              Anexos
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Fotos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fotos
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, 'foto')}
                    className="hidden"
                    id="fotos-upload"
                  />
                  <label
                    htmlFor="fotos-upload"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Camera size={24} className="text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600">Clique para adicionar fotos</span>
                  </label>
                </div>
                
                {fotos.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {fotos.map((foto, index) => (
                      <div key={index} className="flex justify-between items-center bg-white p-2 rounded border">
                        <span className="text-sm truncate">{foto.nome}</span>
                        <button
                          type="button"
                          onClick={() => removeAnexo(index, 'foto')}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Documentos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Documentos
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                    onChange={(e) => handleFileUpload(e, 'documento')}
                    className="hidden"
                    id="documentos-upload"
                  />
                  <label
                    htmlFor="documentos-upload"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <FileText size={24} className="text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600">Clique para adicionar documentos</span>
                  </label>
                </div>
                
                {documentos.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {documentos.map((doc, index) => (
                      <div key={index} className="flex justify-between items-center bg-white p-2 rounded border">
                        <span className="text-sm truncate">{doc.nome}</span>
                        <button
                          type="button"
                          onClick={() => removeAnexo(index, 'documento')}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Outros Anexos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Outros Anexos
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <input
                    type="file"
                    multiple
                    onChange={(e) => handleFileUpload(e, 'anexo')}
                    className="hidden"
                    id="anexos-upload"
                  />
                  <label
                    htmlFor="anexos-upload"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Upload size={24} className="text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600">Clique para adicionar arquivos</span>
                  </label>
                </div>
                
                {anexos.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {anexos.map((anexo, index) => (
                      <div key={index} className="flex justify-between items-center bg-white p-2 rounded border">
                        <span className="text-sm truncate">{anexo.nome}</span>
                        <button
                          type="button"
                          onClick={() => removeAnexo(index, 'anexo')}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Observações */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Observações Gerais</h3>
            <textarea
              value={formData.observacoes}
              onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
              rows={4}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              placeholder="Observações sobre o andamento da obra, ocorrências, pendências..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-4 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {initialData ? 'Atualizar' : 'Criar'} Diário
            </button>
          </div>
        </form>

        {/* Modal Novo Equipamento */}
        {showEquipamentoForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-md p-6">
              <h3 className="text-lg font-semibold mb-4">Novo Equipamento</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                  <input
                    type="text"
                    value={novoEquipamento.nome}
                    onChange={(e) => setNovoEquipamento(prev => ({ ...prev, nome: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                  <input
                    type="text"
                    value={novoEquipamento.tipo}
                    onChange={(e) => setNovoEquipamento(prev => ({ ...prev, tipo: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
                  <input
                    type="text"
                    value={novoEquipamento.marca}
                    onChange={(e) => setNovoEquipamento(prev => ({ ...prev, marca: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Modelo</label>
                  <input
                    type="text"
                    value={novoEquipamento.modelo}
                    onChange={(e) => setNovoEquipamento(prev => ({ ...prev, modelo: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valor/Hora</label>
                  <input
                    type="number"
                    step="0.01"
                    value={novoEquipamento.valorHora}
                    onChange={(e) => setNovoEquipamento(prev => ({ ...prev, valorHora: Number(e.target.value) }))}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setShowEquipamentoForm(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleCreateEquipamento}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Criar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default DiarioObraForm
